import { colorForVehicle } from "@/lib/vehicleColors";

/**
 * One-click chip filter row for table headers.
 *
 *   [ All · 133 ] [ Tesla Model Y · 87 ] [ Hyundai Ioniq 5 · 46 ]
 *
 * Mirrors the global Vehicle Context dropdown but stays in-context above its
 * table. Renders nothing when the household only has one vehicle (no useful
 * picker). Active chip uses the vehicle's stable slot color; inactive chips
 * stay neutral with the slot color reduced to a small dot. Each chip carries
 * a tiny counter showing how many rows that vehicle contributes to the
 * currently-loaded dataset.
 *
 * Props:
 *  - vehicles: ACTIVE vehicle list (archived MUST be excluded by caller).
 *  - value: 'all' | vehicle.id — current selection.
 *  - onChange: (next) => void
 *  - counts: { [vehicleId]: number } — per-vehicle row counts in the current dataset.
 *  - totalCount: number — used to label the "All" chip.
 *  - testIdPrefix: e.g. 'trip' → 'trip-vehicle-chip-{id|all}'
 */
export default function VehicleChipFilter({
  vehicles = [],
  value = "all",
  onChange,
  counts = {},
  totalCount = null,
  testIdPrefix = "vehicle",
}) {
  if (!vehicles || vehicles.length <= 1) return null;

  const totalLabel =
    totalCount == null
      ? null
      : Object.values(counts).reduce((a, b) => a + b, 0) || totalCount;

  const chips = [
    { id: "all", name: "All", color: null, count: totalCount },
    ...vehicles.map((v) => ({
      id: v.id,
      name: v.name,
      color: colorForVehicle(v.id, vehicles),
      count: counts[v.id] ?? 0,
    })),
  ];

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid={`${testIdPrefix}-vehicle-chip-row`}
      role="tablist"
      aria-label="Filter by vehicle"
    >
      {chips.map((chip) => {
        const active = value === chip.id;
        const baseStyle =
          active && chip.color
            ? {
                background: chip.color.soft,
                borderColor: chip.color.border,
                color: chip.color.text,
              }
            : active
              ? {
                  background: "rgb(246, 57, 63)",
                  borderColor: "rgba(232, 33, 39, 1)",
                  color: "#FFFFFF",
                }
              : { background: "#ffffff", borderColor: "#E82127", color: "#000000" };
        const countStyle = {
          color: active && chip.color ? chip.color.text : "#000000",
          opacity: active ? 0.95 : 0.65,
        };
        const displayCount = chip.id === "all" ? totalLabel : chip.count;
        return (
          <button
            key={chip.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange?.(chip.id)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] tracking-tight border transition-colors duration-150 hover:text-[var(--text-primary)]"
            style={{ ...baseStyle, borderRadius: 999 }}
            data-testid={`${testIdPrefix}-vehicle-chip-${chip.id}`}
          >
            {chip.color && (
              <span
                className="inline-block w-1.5 h-1.5 shrink-0"
                style={{ background: chip.color.fill, borderRadius: 1 }}
              />
            )}
            <span className="truncate max-w-[140px]">{chip.name}</span>
            {displayCount != null && (
              <span
                className="font-mono-data text-[10px] tracking-tight"
                style={countStyle}
                data-testid={`${testIdPrefix}-vehicle-chip-count-${chip.id}`}
              >
                · {displayCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
